import React, { useState, useEffect } from 'react';
import { Search, Download, ClipboardCheck, Eye, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Combobox } from '../../../components/ui/combobox';
import { cn } from '../../../components/ui/utils';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { AssetInspection, AssetType } from '../../../types/asset';
import { toSydneyDate } from '../../../utils/helpers';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../../constants/permissions.constants";

const ASSET_TYPES: AssetType[] = ['Vehicle', 'Plant & Equipment', 'Tools', 'Lifting/Slings', 'IT Equipment', 'Mobile Devices', 'Furniture', 'Other'];

// Radix Select rejects an empty-string item value, so "All Assets" needs a sentinel.
const ALL_ASSETS = 'all';

const AssetInspectionsReport: React.FC = () => {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const canReadInspections = ability.can(ACTIONS.READ, RESOURCES.ASSET_INSPECTION);
    const [inspections, setInspections] = useState<AssetInspection[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All Types');
    const [assetFilter, setAssetFilter] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!canReadInspections) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await api.get<AssetInspection[]>(`/api/assets/inspections`);
                setInspections(response || []);
            } catch (error) {
                console.error('Error fetching asset inspections for report:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [canReadInspections]);

    // Only assets with inspections on record are offered, so every option yields rows.
    const assetOptions = [
        { label: 'All Assets', value: ALL_ASSETS },
        ...Array.from(
            inspections
                .filter(inspection => inspection.asset && (typeFilter === 'All Types' || inspection.asset.type === typeFilter))
                .reduce((options, inspection) => {
                    options.set(String(inspection.asset.id), `${inspection.asset.name} (${inspection.asset.tag})`);
                    return options;
                }, new Map<string, string>())
        ).map(([value, label]) => ({ label, value }))
    ];

    const filteredInspections = inspections.filter(inspection => {
        const asset = inspection.asset;
        const term = searchQuery.toLowerCase();
        const matchesSearch = !searchQuery || [
            asset?.name,
            asset?.tag,
            asset?.type,
            asset?.organization?.companyName,
            inspection.template?.name,
            inspection.inspectorName,
            inspection.outcome,
            inspection.generalComments
        ].some(value => (value || '').toLowerCase().includes(term));

        const matchesType = typeFilter === 'All Types' || asset?.type === typeFilter;
        const matchesAsset = !assetFilter || String(asset?.id) === assetFilter;

        return matchesSearch && matchesType && matchesAsset;
    });

    const totalInspections = filteredInspections.length;
    const completedCount = filteredInspections.filter(i => i.status === 'Completed').length;
    const draftCount = filteredInspections.filter(i => i.status === 'Draft').length;
    const failedCount = filteredInspections.filter(i => i.outcome === 'Fail').length;

    const getChecklistCounts = (inspection: AssetInspection) => {
        const results = inspection.itemResults || [];
        return {
            total: results.length,
            passed: results.filter(result => result.answer === 'Yes').length,
            failed: results.filter(result => result.answer === 'No').length,
            notApplicable: results.filter(result => result.answer === 'N/A').length,
        };
    };

    const getStatusStyles = (status?: string) => {
        switch (status) {
            case 'Completed': return 'bg-green-50 text-green-600 border-green-100';
            case 'Draft': return 'bg-amber-50 text-amber-600 border-amber-100';
            default: return 'bg-gray-50 text-gray-600 border-gray-100';
        }
    };

    const getOutcomeStyles = (outcome?: string) => {
        switch (outcome) {
            case 'Pass': return 'bg-green-100 text-green-700 border-green-100';
            case 'Pass with Notes': return 'bg-amber-100 text-amber-700 border-amber-100';
            case 'Fail': return 'bg-red-100 text-red-700 border-red-100';
            default: return 'bg-gray-100 text-gray-500 border-gray-200';
        }
    };

    const downloadCSV = () => {
        const headers = ['Inspection Date', 'Asset ID', 'Asset Name', 'Asset Type', 'Organisation', 'Template', 'Inspector', 'Status', 'Outcome', 'Checklist Passed', 'Checklist Failed', 'Checklist N/A', 'Checklist Total', 'Comments'];
        const rows = filteredInspections.map(inspection => {
            const { total, passed, failed, notApplicable } = getChecklistCounts(inspection);
            return [
                toSydneyDate(inspection.inspectionDate) || '-',
                inspection.asset?.tag || '-',
                inspection.asset?.name || '-',
                inspection.asset?.type || '-',
                inspection.asset?.organization?.companyName || '-',
                inspection.template?.name || '-',
                inspection.inspectorName || '-',
                inspection.status || '-',
                inspection.outcome || '-',
                passed,
                failed,
                notApplicable,
                total,
                inspection.generalComments || ''
            ];
        });

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `asset-inspections-report-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <ClipboardCheck size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Asset Inspections Report</h3>
                            <p className="text-gray-500 text-sm font-medium">Every inspection conducted across all assets, filterable by asset or asset type</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={downloadCSV}>
                            <Download size={16} />
                            <span>CSV</span>
                        </Button>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="mt-6 flex flex-col md:flex-row items-stretch md:items-center gap-4">
                    <div className="relative w-full flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search inspections..."
                            className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium w-full"
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                        <div className="w-full sm:w-64">
                            <Combobox
                                options={assetOptions}
                                value={assetFilter ?? ALL_ASSETS}
                                onChange={(value) => setAssetFilter(!value || value === ALL_ASSETS ? undefined : value)}
                                placeholder="All Assets"
                                searchPlaceholder="Search assets..."
                                emptyMessage="No asset found."
                            />
                        </div>
                        <div className="w-full sm:w-48">
                            <Select value={typeFilter} onValueChange={setTypeFilter}>
                                <SelectTrigger className="h-11 px-4 rounded-xl border-gray-100 bg-gray-50/50 text-sm font-bold text-gray-700 w-full">
                                    <SelectValue placeholder="All Types" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                                    <SelectItem value="All Types" className="font-bold">All Types</SelectItem>
                                    {ASSET_TYPES.map(type => (
                                        <SelectItem key={type} value={type} className="font-bold">{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="mt-6 bg-gray-50 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Inspections</p>
                        <h3 className="text-2xl font-black text-gray-900">{totalInspections}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Completed</p>
                        <h3 className="text-2xl font-black text-green-600">{completedCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Drafts</p>
                        <h3 className="text-2xl font-black text-amber-600">{draftCount}</h3>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Failed</p>
                        <h3 className="text-2xl font-black text-red-600">{failedCount}</h3>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto no-scrollbar pb-4">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-white border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Inspection Date</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Asset Type</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Template</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Inspector</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Outcome</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Checklist</th>
                            <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-800 uppercase tracking-wider">Comments</th>
                            <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-800 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td colSpan={10} className="px-6 py-4"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                </tr>
                            ))
                        ) : !canReadInspections ? (
                            <tr>
                                <td colSpan={10} className="px-6 py-12 text-center text-gray-400">
                                    You do not have permission to view asset inspections.
                                </td>
                            </tr>
                        ) : filteredInspections.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="px-6 py-12 text-center text-gray-400">
                                    No inspections found matching the criteria.
                                </td>
                            </tr>
                        ) : (
                            filteredInspections.map((inspection) => {
                                const checklist = getChecklistCounts(inspection);
                                return (
                                    <tr key={inspection.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {toSydneyDate(inspection.inspectionDate) || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="min-w-[160px]">
                                                <p className="font-bold text-gray-900 text-[13px]">{inspection.asset?.name || '-'}</p>
                                                <p className="text-[11px] font-bold text-gray-400 font-mono tracking-wider mt-0.5">{inspection.asset?.tag || '-'}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {inspection.asset?.type || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {inspection.template?.name || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {inspection.inspectorName || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2 py-1 text-[11px] whitespace-nowrap", getStatusStyles(inspection.status))}>
                                                {inspection.status === 'Completed' ? (
                                                    <span className="flex items-center gap-1.5">
                                                        <CheckCircle2 size={12} /> Completed
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1.5">
                                                        <AlertTriangle size={12} /> {inspection.status}
                                                    </span>
                                                )}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant="outline" className={cn("rounded-lg border-0 font-bold px-2 py-1 text-[11px] whitespace-nowrap", getOutcomeStyles(inspection.outcome))}>
                                                {inspection.outcome === 'Fail' && <XCircle size={12} className="mr-1.5" />}
                                                {inspection.outcome || 'Pending'}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-700 whitespace-nowrap">
                                            {checklist.total === 0 ? '-' : `${checklist.passed}/${checklist.total} passed`}
                                            {checklist.failed > 0 && (
                                                <span className="text-red-600 font-bold"> · {checklist.failed} failed</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-[13px] font-medium text-gray-500 max-w-[240px] truncate" title={inspection.generalComments || ''}>
                                            {inspection.generalComments || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                                onClick={() => navigate(`/assets/${inspection.asset?.id}/inspections/${inspection.id}`)}
                                                title="View Inspection"
                                                disabled={!inspection.asset}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AssetInspectionsReport;
